import React, { useContext, useEffect, useState } from "react";
import {
  FormProvider,
  useForm,
  type UseFormGetValues,
  type UseFormSetValue,
} from "react-hook-form";
import { useModal } from "react-modal-hook";
import { useHistory } from "react-router";
import { CREATOR_AND_UP, GUEST } from "common/authorization";
import { Attachment } from "common/utils/attachments";
import { isNotNil } from "common/utils/tools";
import { AuthContext } from "../../../../Authorization/AuthContext";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
} from "../../../../Common/__styles__/Modal";
import { Button } from "../../../../Common/Buttons/Button";
import Modal from "../../../../Common/Modal";
import { Label } from "../../../../Inputs";
import { Required } from "../../../../Inputs/__styles__/Label";
import { Select, Text } from "../../../../Inputs/react-hook-form";
import { SUBMISSION_CATEGORY_OPTIONS } from "../../../../Submissions/utils";
import { InputGrid } from "../../PropertyData/__styles__/floodInfoModals";
import { RequiredMessage } from "../../PropertyData/__styles__/upsertDfeRuleModal";
import { AttachmentSelect } from "./AttachmentSelect";
import { type SubmissionsBuilderFormData } from "./form/form";
import { RoleAndGroupSelector } from "./RoleAndGroupSelector";
import { useSubmissionsBuilderContext } from "./SubmissionsBuilderContext";
import type { ExistingSubmissionType } from "./types";
import { onCanCreateChange } from "./utils";

type CreateSubmissionTypeFormData = Pick<
  SubmissionsBuilderFormData,
  "name" | "category" | "canCreateRolesAndGroupIds" | "attachments"
>;

type CreateSubmissionTypeModalProps = {
  onCancel: () => void;
  onSubmit: (values: CreateSubmissionTypeFormData) => void;
};

function useCreateSubmissionTypeModal({
  onSubmit,
}: Pick<CreateSubmissionTypeModalProps, "onSubmit">) {
  const history = useHistory();
  const [show, hide] = useModal(
    () => (
      <Modal>
        <CreateSubmissionTypeModal
          onCancel={() => history.push("/settings/account/records")}
          onSubmit={(values: CreateSubmissionTypeFormData) => {
            onSubmit(values);
            hide();
          }}
        />
      </Modal>
    ),
    [onSubmit],
  );

  return [show, hide] as const;
}

function CreateSubmissionTypeModal({
  onCancel,
  onSubmit,
}: CreateSubmissionTypeModalProps) {
  // we're using a new form here because we want a subset of the fields and to
  // have the nicety of things like errors and a submit button that is disabled
  // until the form is valid
  const formMethods = useForm<CreateSubmissionTypeFormData>();
  const { account } = useContext(AuthContext);
  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
    register,
  } = formMethods;

  const [portalContainerRef, setPortalContainerRef] =
    useState<HTMLDivElement | null>(null);

  // we don't get JSON schema validation for free, so have to hand-roll
  useEffect(() => {
    register("attachments", {
      validate: {
        minLength: (value: Maybe<Attachment[]>) =>
          value && value.length > 0 ? true : "Must NOT have fewer than 1 items",
      },
    });
  });

  return (
    <Container overflows>
      <HeaderSection>
        <h1>Add record type</h1>
      </HeaderSection>
      <FormProvider {...formMethods}>
        <FormSection overflows>
          <ContentSection overflows>
            <RequiredMessage>
              All fields are required<Required>*</Required>
            </RequiredMessage>
            <InputGrid
              style={{ marginTop: "24px" }}
              ref={setPortalContainerRef}
            >
              <Text
                label="Record type"
                error={errors.name?.message}
                {...register("name", { required: "Record type is required" })}
              />
              <div>
                <Label text="Record category" htmlFor="category" />
                <Select
                  control={control}
                  name="category"
                  options={SUBMISSION_CATEGORY_OPTIONS}
                  size="medium"
                  required={true}
                  error={errors.category?.message}
                  rules={{ required: "Record category is required" }}
                />
              </div>
              <RoleAndGroupSelector
                control={control}
                label="Who can fill out this form?"
                portalContainerRef={portalContainerRef}
                name="canCreateRolesAndGroupIds"
                options={{
                  roles: [...CREATOR_AND_UP, ...GUEST],
                  userGroups: account?.userGroups.filter(isNotNil) ?? [],
                }}
                rules={{ required: "This field is required" }}
                error={errors.canCreateRolesAndGroupIds?.message}
              />
              <AttachmentSelect disabled={false} />
            </InputGrid>
          </ContentSection>
        </FormSection>
      </FormProvider>
      <ButtonSection>
        <PrimaryButtons>
          <Button
            size="medium"
            variant="secondary"
            onPress={onCancel}
            title="Cancel"
          />
          <Button
            size="medium"
            variant="primary"
            onPress={() => handleSubmit(onSubmit)()}
            isDisabled={!isValid}
            data-testid="add-submission-type-button"
            title="Add"
          />
        </PrimaryButtons>
      </ButtonSection>
    </Container>
  );
}

type UseShowCreateSubmissionTypeModalOnEnterParams = {
  existingSubmissionType?: Maybe<ExistingSubmissionType>;
  setValue: UseFormSetValue<SubmissionsBuilderFormData>;
  getValues: UseFormGetValues<SubmissionsBuilderFormData>;
};

export function useShowCreateSubmissionTypeModalOnEnter({
  existingSubmissionType,
  setValue,
  getValues,
}: UseShowCreateSubmissionTypeModalOnEnterParams) {
  const { setCanCreateGroups, setCanCreateRoles } =
    useSubmissionsBuilderContext();
  const { account } = useContext(AuthContext);
  const accountUserGroups = account?.userGroups ?? [];

  const [showCreateSubmissionTypeModal] = useCreateSubmissionTypeModal({
    onSubmit: ({
      name,
      category,
      attachments,
      canCreateRolesAndGroupIds,
    }: CreateSubmissionTypeFormData) => {
      setValue("name", name);
      setValue("category", category);
      setValue("canCreateRolesAndGroupIds", canCreateRolesAndGroupIds);
      setValue("attachments", attachments);
      onCanCreateChange({
        getFormValues: getValues,
        setFormValue: setValue,
        setCanCreateGroups,
        setCanCreateRoles,
        accountUserGroups,
      });
    },
  });

  useEffect(() => {
    if (!existingSubmissionType) {
      showCreateSubmissionTypeModal();
    }
  }, [showCreateSubmissionTypeModal, existingSubmissionType]);
}
